import React, { useState, useEffect } from 'react';
import { Title, useTranslate, useNotify, useRefresh, useDataProvider, useGetOne } from 'react-admin';
import { useMutation } from 'react-query';
import { CodeSnippet } from '../common_modules/components/CodeSnippet';
import { scriptData } from '../common_modules/components/tracking/trackingScript';
import { Button, TextField, Typography, Box, CircularProgress, Chip, Alert, RadioGroup, Radio, FormControlLabel, FormControl, FormLabel } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

// =============================================================================
// API連携状況の表示とテスト実行を行うコンポーネント
// =============================================================================
interface ApiConnectionStatusProps {
  clientId: string;
  ecSiteType: string | null;
}

const ApiConnectionStatus: React.FC<ApiConnectionStatusProps> = ({ clientId, ecSiteType }) => {
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const refresh = useRefresh();

  const { data: client, isLoading: isClientLoading } = useGetOne('settings', { id: clientId }, { onError: () => {} });

  const { mutate: testConnection, isLoading: isTesting } = useMutation(
    () => dataProvider.testConnection({ clientId }),
    {
      onSuccess: (response) => {
        const result = (response as { json: { success: boolean; message?: string } }).json;
        if (result.success) {
          notify('API連携に成功しました。', { type: 'success' });
        } else {
          notify(result.message || 'API連携の確認に失敗しました。', { type: 'error' });
        }
        refresh();
      },
      onError: (error: any) => {
        notify(error.message || 'テスト実行中にエラーが発生しました。', { type: 'error' });
        refresh();
      }
    }
  );

  const handleTestConnection = () => {
    testConnection();
  };

  const renderStatusChip = () => {
    if (isClientLoading) {
      return <Chip label="連携状態を確認中..." size="small" />;
    }
    if (client?.apiRelationFlag) {
      return <Chip icon={<CheckCircleIcon />} label="連携済み" color="success" variant="outlined" size="small" />;
    }
    return <Chip icon={<CancelIcon />} label="未連携" color="error" variant="outlined" size="small" />;
  };

  // const shouldTest = ecSiteType !== '5';
  const shouldTest = true;

  return (
    <Box sx={{ border: '1px solid #e0e0e0', padding: 2, marginY: 2, borderRadius: '4px', backgroundColor: '#f9f9f9' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6">API連携ステータス:</Typography>
          {renderStatusChip()}
        </Box>
        {shouldTest && (
          <Button variant="contained" onClick={handleTestConnection} disabled={isTesting}>
            {isTesting ? <CircularProgress size={24} color="inherit" /> : 'API連携をテストする'}
          </Button>
        )}
      </Box>
      {!client?.apiRelationFlag && shouldTest && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          API認証情報（アクセストークン等）を保存後、必ずこのテストを実行して連携を完了させてください。
        </Alert>
      )}
    </Box>
  );
};

// =============================================================================
// ▼▼▼ Shopify連携フォームコンポーネント ▼▼▼
// =============================================================================
interface ShopifyAuthFormProps {
  clientId: string;
}

const ShopifyAuthForm: React.FC<ShopifyAuthFormProps> = ({ clientId }) => {
  const [authMode, setAuthMode] = useState<'devDashboard' | 'legacy'>('devDashboard');
  // レガシー方式用
  const [accessToken, setAccessToken] = useState('');
  // Dev Dashboard方式用
  const [shopifyClientId, setShopifyClientId] = useState('');
  const [shopifyClientSecret, setShopifyClientSecret] = useState('');
  // 共通
  const [managementUrl, setManagementUrl] = useState('');
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();

  // レガシー方式: アクセストークン保存
  const { mutate: mutateToken, isLoading: isTokenLoading } = useMutation(
    (variables: { accessToken: string }) =>
      dataProvider.updateEcCredentials({
        clientId: clientId,
        data: { accessToken: variables.accessToken }
      }),
    {
      onSuccess: () => {
        notify('アクセストークンが正常に保存されました。', { type: 'success' });
        setAccessToken('');
        refresh();
      },
      onError: (error: any) => {
        notify(error.message || 'トークンの保存中にエラーが発生しました。', { type: 'error' });
      }
    }
  );

  // レガシー方式: URL保存
  const { mutate: mutateUrl, isLoading: isUrlLoading } = useMutation(
    (variables: { managementUrl: string }) =>
      dataProvider.updateEcCredentials({
        clientId: clientId,
        data: { managementUrl: variables.managementUrl }
      }),
    {
      onSuccess: () => {
        notify('Shopify管理画面URLが正常に保存されました。', { type: 'success' });
        setManagementUrl('');
        refresh();
      },
      onError: (error: any) => {
        notify(error.message || 'URLの保存中にエラーが発生しました。', { type: 'error' });
      }
    }
  );

  // Dev Dashboard方式: Client ID + Client Secret + URL 一括保存
  const { mutate: mutateDevDashboard, isLoading: isDevDashboardLoading } = useMutation(
    (variables: { shopifyClientId: string; shopifyClientSecret: string; managementUrl: string }) =>
      dataProvider.updateEcCredentials({
        clientId: clientId,
        data: {
          shopifyClientId: variables.shopifyClientId,
          shopifyClientSecret: variables.shopifyClientSecret,
          managementUrl: variables.managementUrl,
        }
      }),
    {
      onSuccess: () => {
        notify('Shopify API連携情報が正常に保存されました。', { type: 'success' });
        setShopifyClientId('');
        setShopifyClientSecret('');
        setManagementUrl('');
        refresh();
      },
      onError: (error: any) => {
        notify(error.message || 'API連携情報の保存中にエラーが発生しました。', { type: 'error' });
      }
    }
  );

  const handleSaveToken = () => {
    if (!accessToken.trim()) {
      notify('アクセストークンを入力してください。', { type: 'warning' });
      return;
    }
    mutateToken({ accessToken });
  };

  const handleSaveUrl = () => {
    if (!managementUrl.trim()) {
      notify('Shopify管理画面URLを入力してください。', { type: 'warning' });
      return;
    }
    mutateUrl({ managementUrl });
  };

  const handleSaveDevDashboard = () => {
    if (!shopifyClientId.trim() || !shopifyClientSecret.trim() || !managementUrl.trim()) {
      notify('Client ID、Client Secret、管理画面URLをすべて入力してください。', { type: 'warning' });
      return;
    }
    mutateDevDashboard({ shopifyClientId, shopifyClientSecret, managementUrl });
  };

  return (
    <Box sx={{ border: '1px solid #ddd', padding: 3, marginY: 2, borderRadius: '4px' }}>
      <Typography variant="h5" gutterBottom>
        Shopify API連携設定
      </Typography>

      <FormControl sx={{ mb: 2 }}>
        <FormLabel>アプリの種類</FormLabel>
        <RadioGroup
          row
          value={authMode}
          onChange={(e) => setAuthMode(e.target.value as 'devDashboard' | 'legacy')}
        >
          <FormControlLabel value="devDashboard" control={<Radio />} label="Dev Dashboard" />
          <FormControlLabel value="legacy" control={<Radio />} label="レガシー" />
        </RadioGroup>
      </FormControl>

      {authMode === 'devDashboard' ? (
        /* Dev Dashboard方式 */
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4 }}>
          <Typography paragraph>
            Dev Dashboardで作成したアプリのクライアントIDとシークレット、Shopifyの管理画面URLを入力して保存してください。
          </Typography>
          <TextField
            label="クライアントID"
            variant="outlined"
            fullWidth
            value={shopifyClientId}
            onChange={(e) => setShopifyClientId(e.target.value)}
            disabled={isDevDashboardLoading}
          />
          <TextField
            label="シークレット"
            variant="outlined"
            fullWidth
            value={shopifyClientSecret}
            onChange={(e) => setShopifyClientSecret(e.target.value)}
            disabled={isDevDashboardLoading}
          />
          <TextField
            label="Shopify管理画面URL"
            placeholder="例: https://admin.shopify.com/store/your-store-name"
            variant="outlined"
            fullWidth
            value={managementUrl}
            onChange={(e) => setManagementUrl(e.target.value)}
            disabled={isDevDashboardLoading}
          />
          <Button variant="contained" onClick={handleSaveDevDashboard} disabled={isDevDashboardLoading} sx={{ mt: 1, alignSelf: 'flex-start' }}>
            {isDevDashboardLoading ? <CircularProgress size={24} color="inherit" /> : '保存'}
          </Button>
        </Box>
      ) : (
        /* レガシー方式 */
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4 }}>
          <Typography paragraph>
            Shopifyアプリから発行されたアクセストークンと、Shopifyの管理画面URLをそれぞれ入力・保存してください。
          </Typography>
          <Box>
            <TextField
              label="Admin API アクセストークン"
              variant="outlined"
              fullWidth
              value={accessToken}
              onChange={(e) => setAccessToken(e.target.value)}
              disabled={isTokenLoading}
            />
            <Button variant="contained" onClick={handleSaveToken} disabled={isTokenLoading} sx={{ mt: 1 }}>
              {isTokenLoading ? <CircularProgress size={24} color="inherit" /> : 'トークンを保存'}
            </Button>
          </Box>
          <Box>
            <TextField
              label="Shopify管理画面URL"
              placeholder="例: https://admin.shopify.com/store/your-store-name"
              variant="outlined"
              fullWidth
              value={managementUrl}
              onChange={(e) => setManagementUrl(e.target.value)}
              disabled={isUrlLoading}
            />
            <Button variant="contained" onClick={handleSaveUrl} disabled={isUrlLoading} sx={{ mt: 1 }}>
              {isUrlLoading ? <CircularProgress size={24} color="inherit" /> : 'URLを保存'}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

// =============================================================================
// ▼▼▼ BASE連携フォームコンポーネント▼▼▼
// =============================================================================
const BASE_INSTALL_URL =
  'https://admin.thebase.com/oauths/authorize?response_type=code&client_id=80b6ad092e4932f356eda0adc5ec5a21&redirect_uri=https%3A%2F%2Fsuccessfee.com%2F&scope=read_items%20read_orders&state=successfee';

interface BaseAuthFormProps {
  clientId: string;
}

const BaseAuthForm: React.FC<BaseAuthFormProps> = ({ clientId }) => {
  const [redirectUrl, setRedirectUrl] = useState('');
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();

  const { mutate, isLoading } = useMutation(
    (variables: { redirectUrl: string }) =>
      dataProvider.updateEcCredentials({
        clientId: clientId,
        data: { redirectUrl: variables.redirectUrl }
      }),
    {
      onSuccess: () => {
        notify('設定が正常に保存されました。', { type: 'success' });
        setRedirectUrl('');
        refresh();
      },
      onError: (error: any) => {
        const errorMsg = error.body?.errors?.redirectUrl || error.message || 'エラーが発生しました。';
        notify(errorMsg, { type: 'error' });
      }
    }
  );

  const handleSave = () => {
    if (!redirectUrl.trim()) {
      notify('リダイレクトURLを入力してください。', { type: 'warning' });
      return;
    }
    mutate({ redirectUrl });
  };

  return (
    <Box sx={{ border: '1px solid #ddd', padding: 3, marginY: 2, borderRadius: '4px' }}>
      <Typography variant="h5" gutterBottom>
        BASE API連携設定
      </Typography>
      <Box sx={{ marginBottom: 3 }}>
        <Typography variant="h6">ステップ1: アプリをインストール</Typography>
        <Typography paragraph>
          以下のボタンをクリックして、BASEショップにAPI連携用アプリをインストールしてください。
        </Typography>
        <Button variant="contained" color="primary" onClick={() => window.open(BASE_INSTALL_URL, '_blank')}>
          BASEアプリをインストール
        </Button>
      </Box>
      <Box>
        <Typography variant="h6">ステップ2: リダイレクトURLを登録</Typography>
        <Typography paragraph>
          認証後にリダイレクトされたページのURL全体をコピーし、以下に貼り付けて保存してください。
        </Typography>
        <TextField
          label="リダイレクトされたページのURL"
          variant="outlined"
          fullWidth
          value={redirectUrl}
          onChange={(e) => setRedirectUrl(e.target.value)}
          sx={{ marginBottom: 1 }}
          disabled={isLoading}
        />
        <Button variant="contained" onClick={handleSave} disabled={isLoading}>
          {isLoading ? <CircularProgress size={24} color="inherit" /> : 'URLを保存'}
        </Button>
      </Box>
    </Box>
  );
};

// =============================================================================
// ▼▼▼ STORES連携フォームコンポーネント▼▼▼
// =============================================================================
interface StoresAuthFormProps {
  clientId: string;
}

const StoresAuthForm: React.FC<StoresAuthFormProps> = ({ clientId }) => {
  const [redirectUrl, setRedirectUrl] = useState('');
  const [authUrl, setAuthUrl] = useState<string | null>(null);
  const [isLoadingAuthUrl, setIsLoadingAuthUrl] = useState(false);
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();

  // 認証URLをSSMから取得
  useEffect(() => {
    const fetchAuthUrl = async () => {
      setIsLoadingAuthUrl(true);
      try {
        const response = await dataProvider.getAuthUrl({ clientId });
        setAuthUrl(response.data.authUrl);
      } catch (error: any) {
        console.error('Failed to fetch auth URL:', error);
        notify('認証URLの取得に失敗しました。', { type: 'error' });
      } finally {
        setIsLoadingAuthUrl(false);
      }
    };
    fetchAuthUrl();
  }, [clientId, dataProvider, notify]);

  const { mutate, isLoading } = useMutation(
    (variables: { redirectUrl: string }) =>
      dataProvider.updateEcCredentials({
        clientId: clientId,
        data: { redirectUrl: variables.redirectUrl }
      }),
    {
      onSuccess: () => {
        notify('設定が正常に保存されました。', { type: 'success' });
        setRedirectUrl('');
        refresh();
      },
      onError: (error: any) => {
        const errorMsg = error.body?.errors?.redirectUrl || error.message || 'エラーが発生しました。';
        notify(errorMsg, { type: 'error' });
      }
    }
  );

  const handleSave = () => {
    if (!redirectUrl.trim()) {
      notify('リダイレクトURLを入力してください。', { type: 'warning' });
      return;
    }
    mutate({ redirectUrl });
  };

  return (
    <Box sx={{ border: '1px solid #ddd', padding: 3, marginY: 2, borderRadius: '4px' }}>
      <Typography variant="h5" gutterBottom>
        STORES API連携設定
      </Typography>
      <Box sx={{ marginBottom: 3 }}>
        <Typography variant="h6">ステップ1: アプリをインストール</Typography>
        <Typography paragraph>
          以下のボタンをクリックして、STORESにAPI連携用アプリをインストールしてください。
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => authUrl && window.open(authUrl, '_blank')}
          disabled={isLoadingAuthUrl || !authUrl}
        >
          {isLoadingAuthUrl ? <CircularProgress size={24} color="inherit" /> : 'STORESアプリをインストール'}
        </Button>
      </Box>
      <Box>
        <Typography variant="h6">ステップ2: リダイレクトURLを登録</Typography>
        <Typography paragraph>
          認証後にリダイレクトされたページのURL全体をコピーし、以下に貼り付けて保存してください。
        </Typography>
        <TextField
          label="リダイレクトされたページのURL"
          variant="outlined"
          fullWidth
          value={redirectUrl}
          onChange={(e) => setRedirectUrl(e.target.value)}
          sx={{ marginBottom: 1 }}
          disabled={isLoading}
        />
        <Button variant="contained" onClick={handleSave} disabled={isLoading}>
          {isLoading ? <CircularProgress size={24} color="inherit" /> : 'URLを保存'}
        </Button>
      </Box>
    </Box>
  );
};

// =============================================================================
// ▼▼▼ カラーミーショップ連携フォームコンポーネント▼▼▼
// =============================================================================
interface ColormeAuthFormProps {
  clientId: string;
}

const ColormeAuthForm: React.FC<ColormeAuthFormProps> = ({ clientId }) => {
  const [redirectUrl, setRedirectUrl] = useState('');
  const [authUrl, setAuthUrl] = useState<string | null>(null);
  const [isLoadingAuthUrl, setIsLoadingAuthUrl] = useState(false);
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();

  // 認証URLをSSMから取得
  useEffect(() => {
    const fetchAuthUrl = async () => {
      setIsLoadingAuthUrl(true);
      try {
        const response = await dataProvider.getAuthUrl({ clientId });
        setAuthUrl(response.data.authUrl);
      } catch (error: any) {
        console.error('Failed to fetch auth URL:', error);
        notify('認証URLの取得に失敗しました。', { type: 'error' });
      } finally {
        setIsLoadingAuthUrl(false);
      }
    };
    fetchAuthUrl();
  }, [clientId, dataProvider, notify]);

  const { mutate, isLoading } = useMutation(
    (variables: { redirectUrl: string }) =>
      dataProvider.updateEcCredentials({
        clientId: clientId,
        data: { redirectUrl: variables.redirectUrl }
      }),
    {
      onSuccess: () => {
        notify('設定が正常に保存されました。', { type: 'success' });
        setRedirectUrl('');
        refresh();
      },
      onError: (error: any) => {
        const errorMsg = error.body?.errors?.redirectUrl || error.message || 'エラーが発生しました。';
        notify(errorMsg, { type: 'error' });
      }
    }
  );

  const handleSave = () => {
    if (!redirectUrl.trim()) {
      notify('リダイレクトURLを入力してください。', { type: 'warning' });
      return;
    }
    mutate({ redirectUrl });
  };

  return (
    <Box sx={{ border: '1px solid #ddd', padding: 3, marginY: 2, borderRadius: '4px' }}>
      <Typography variant="h5" gutterBottom>
        カラーミーショップ API連携設定
      </Typography>
      <Box sx={{ marginBottom: 3 }}>
        <Typography variant="h6">ステップ1: アプリをインストール</Typography>
        <Typography paragraph>
          以下のボタンをクリックして、カラーミーショップにAPI連携用アプリをインストールしてください。
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => authUrl && window.open(authUrl, '_blank')}
          disabled={isLoadingAuthUrl || !authUrl}
        >
          {isLoadingAuthUrl ? <CircularProgress size={24} color="inherit" /> : 'カラーミーショップアプリをインストール'}
        </Button>
      </Box>
      <Box>
        <Typography variant="h6">ステップ2: リダイレクトURLを登録</Typography>
        <Typography paragraph>
          認証後にリダイレクトされたページのURL全体をコピーし、以下に貼り付けて保存してください。
        </Typography>
        <TextField
          label="リダイレクトされたページのURL"
          variant="outlined"
          fullWidth
          value={redirectUrl}
          onChange={(e) => setRedirectUrl(e.target.value)}
          sx={{ marginBottom: 1 }}
          disabled={isLoading}
        />
        <Button variant="contained" onClick={handleSave} disabled={isLoading}>
          {isLoading ? <CircularProgress size={24} color="inherit" /> : 'URLを保存'}
        </Button>
      </Box>
    </Box>
  );
};

// =============================================================================
// ▼▼▼ EC-CUBE連携フォームコンポーネント▼▼▼
// =============================================================================
interface EcCubeAuthFormProps {
  clientId: string;
}

const EcCubeAuthForm: React.FC<EcCubeAuthFormProps> = ({ clientId }) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();

  // State
  const [ecCubeClientId, setEcCubeClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const redirectUri = 'https://successfee.com/'; // 固定値
  const [authEndpoint, setAuthEndpoint] = useState('');
  const [generatedUrl, setGeneratedUrl] = useState('');
  const [redirectedUrl, setRedirectedUrl] = useState('');

  // ヘルパー関数: URLからサイトドメイン（ホスト+パス）を抽出
  const extractDomainFromUrl = (url: string): string | null => {
    try {
      const parsedUrl = new URL(url);
      // pathから最後の2つのセグメント（/xxx/authorize）を削除してベースパスを取得
      const pathSegments = parsedUrl.pathname.split('/').filter((seg) => seg); // 空文字列を除外
      if (pathSegments.length >= 2) {
        pathSegments.splice(-2, 2); // 最後の2つ（例: admin, authorize）を削除
      }
      const basePath = pathSegments.length > 0 ? '/' + pathSegments.join('/') + '/' : '/';
      return parsedUrl.hostname + basePath; // 例: "example.com/ec/"
    } catch (error) {
      console.error('Invalid URL:', url);
      return null;
    }
  };

  // 認可URLを生成するためのMutation
  const { mutate: generateUrl, isLoading: isGenerating } = useMutation(
    (params: { clientId: string; clientSecret: string; redirectUri: string; authEndpoint: string }) =>
      dataProvider.updateEcCredentials({
        clientId: clientId,
        data: {
          action: 'generateUrl',
          ecCubeClientId: params.clientId,
          clientSecret: params.clientSecret,
          redirectUri: params.redirectUri,
          authEndpoint: params.authEndpoint
        }
      }),
    {
      onSuccess: (data: any) => {
        setGeneratedUrl(data.data.authorizationUrl);
        notify('連携用URLを発行しました。「EC-CUBE認証画面を開く」ボタンを押してAPIの認証を進めてください。', {
          type: 'success'
        });
      },
      onError: (error: any) => {
        notify(error.message || 'URLの生成に失敗しました。', { type: 'error' });
      }
    }
  );

  // 認証を完了させるためのMutation
  const { mutate: completeAuth, isLoading: isCompleting } = useMutation(
    (params: { redirectedUrl: string; authEndpoint: string }) =>
      dataProvider.updateEcCredentials({
        clientId: clientId, // propsから受け取ったシステム内ID
        data: {
          action: 'completeAuth', // バックエンドで処理を振り分けるためのキー
          redirectedUrl: params.redirectedUrl,
          authEndpoint: params.authEndpoint // バックエンドでドメイン抽出
        }
      }),
    {
      onSuccess: () => {
        notify('API連携が正常に完了しました。', { type: 'success' });
        setRedirectedUrl('');
        refresh();
      },
      onError: (error: any) => {
        const errorMsg =
          error.body?.errors?.redirectedUrl || error.message || '連携に失敗しました。認証情報を再度確認してください。';
        notify(errorMsg, { type: 'error' });
      }
    }
  );

  // 「API連携用URLを発行」ボタンの処理
  const handleGenerateUrl = () => {
    if (!ecCubeClientId.trim() || !clientSecret.trim() || !redirectUri.trim() || !authEndpoint.trim()) {
      notify('全ての項目を入力してください。', { type: 'warning' });
      return;
    }
    // ▼ 変更点 3 ▼
    // generateUrlに渡すパラメータをEC-CUBEの認証情報のみにする
    generateUrl({ clientId: ecCubeClientId, clientSecret, redirectUri, authEndpoint });
  };

  // 「連携」ボタンの処理
  const handleCompleteAuth = () => {
    if (!redirectedUrl.trim()) {
      notify('リダイレクトURLを入力してください。', { type: 'warning' });
      return;
    }
    if (!authEndpoint.trim()) {
      notify('Authorization endpointを入力してください。', { type: 'warning' });
      return;
    }

    // バリデーション: authEndpointからドメインを抽出できるか確認
    const siteDomain = extractDomainFromUrl(authEndpoint);
    if (!siteDomain) {
      notify('Authorization endpointが正しい形式ではありません。', { type: 'error' });
      return;
    }

    completeAuth({ redirectedUrl, authEndpoint });
  };

  return (
    <Box sx={{ border: '1px solid #ddd', padding: 3, marginY: 2, borderRadius: '4px' }}>
      <Typography variant="h5" gutterBottom>
        EC-CUBE API連携設定
      </Typography>

      {/* Step1: 必須プラグインのインストール */}
      <Box sx={{ marginBottom: 4 }}>
        <Typography variant="h6">ステップ1: 必須プラグインのインストール</Typography>

        {/* successfeeプラグイン（統合版） */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            successfeeプラグイン
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
            アフィリエイト連携とクーポン情報の取得に必要なプラグインです。
          </Typography>
          <Button
            variant="contained"
            href={`/downloads/${import.meta.env.VITE_SUCCESSFEE_PLUGIN_FILE || 'successfee-plugin.tar.gz'}`}
            download
            sx={{ textTransform: 'none' }}
          >
            successfee-plugin.tar.gz をダウンロード
          </Button>
        </Box>

        {/* インストール手順 */}
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" component="div">
            <strong>インストール手順:</strong>
            <ol style={{ marginTop: 8, paddingLeft: 20 }}>
              <li>EC-CUBE管理画面にログイン</li>
              <li>オーナーズストア → プラグイン → プラグイン一覧</li>
              <li>ユーザー独自プラグイン →「アップロードして新規追加」→ ダウンロードしたファイルをアップロード</li>
              <li>プラグインを有効化</li>
            </ol>
          </Typography>
        </Box>
      </Box>

      {/* Step2: API連携用URLの発行 */}
      <Box sx={{ marginBottom: 4 }}>
        <Typography variant="h6">ステップ2: API連携用URLの発行</Typography>
        <Typography paragraph>EC-CUBEで発行した認証情報を入力してください。</Typography>
        <TextField
          label="クライアントID (EC-CUBE)"
          fullWidth
          value={ecCubeClientId}
          onChange={(e) => setEcCubeClientId(e.target.value)}
          sx={{ mb: 2 }}
        />
        <TextField
          label="クライアントシークレット"
          type="password"
          fullWidth
          value={clientSecret}
          onChange={(e) => setClientSecret(e.target.value)}
          sx={{ mb: 2 }}
        />
        <TextField
          label="Authorization endpoint (例: https://example.com/authorize)"
          fullWidth
          value={authEndpoint}
          onChange={(e) => setAuthEndpoint(e.target.value)}
          sx={{ mb: 2 }}
        />
        <Button variant="contained" onClick={handleGenerateUrl} disabled={isGenerating}>
          {isGenerating ? <CircularProgress size={24} color="inherit" /> : 'API連携用URLを発行'}
        </Button>
      </Box>

      {/* Step3: EC-CUBE認証画面へ遷移 */}
      <Box sx={{ marginBottom: 4 }}>
        <Typography variant="h6">ステップ3: EC-CUBE認証画面へ遷移</Typography>
        <Typography paragraph variant="body2">
          以下のボタンをクリックしてEC-CUBEの認証画面に移動し、アプリの連携を許可してください。許可すると、EC-CUBEに設定したリダイレクトURIに移動します。
        </Typography>
        <Button
          variant="contained"
          color="primary"
          disabled={!generatedUrl}
          onClick={() => window.open(generatedUrl, '_blank', 'noopener,noreferrer')}
        >
          EC-CUBE認証画面を開く
        </Button>
        {!generatedUrl && (
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            ステップ2を完了するとボタンが有効になります
          </Typography>
        )}
      </Box>

      {/* Step4: リダイレクトURLの登録 */}
      <Box>
        <Typography variant="h6">ステップ4: リダイレクトURLの登録</Typography>
        <Typography paragraph>
          ステップ3で認証後、リダイレクトされたページのURL全体を以下に貼り付けて連携を完了してください。
        </Typography>
        <TextField
          label="リダイレクトされたページのURL"
          fullWidth
          value={redirectedUrl}
          onChange={(e) => setRedirectedUrl(e.target.value)}
          sx={{ mb: 1 }}
        />
        <Button variant="contained" color="primary" onClick={handleCompleteAuth} disabled={isCompleting}>
          {isCompleting ? <CircularProgress size={24} color="inherit" /> : '連携'}
        </Button>
      </Box>
    </Box>
  );
};

// =============================================================================
// ▼▼▼ Makeshop連携フォームコンポーネント ▼▼▼
// =============================================================================
interface MakeshopAuthFormProps {
  clientId: string;
}

const MakeshopAuthForm: React.FC<MakeshopAuthFormProps> = ({ clientId }) => {
  const [shopId, setShopId] = useState('');
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();

  const { mutate, isLoading } = useMutation(
    (variables: { shopId: string }) =>
      dataProvider.updateEcCredentials({
        clientId: clientId,
        data: { shopId: variables.shopId }
      }),
    {
      onSuccess: () => {
        notify('makeshop設定が正常に保存されました。', { type: 'success' });
        setShopId('');
        refresh();
      },
      onError: (error: any) => {
        notify(error.message || 'エラーが発生しました。', { type: 'error' });
      }
    }
  );

  const handleSave = () => {
    if (!shopId.trim()) {
      notify('Shop IDを入力してください。', { type: 'warning' });
      return;
    }
    mutate({ shopId });
  };

  return (
    <Box sx={{ border: '1px solid #ddd', padding: 3, marginY: 2, borderRadius: '4px' }}>
      <Typography variant="h5" gutterBottom>
        makeshop API連携設定
      </Typography>

      <Box>
        <Typography variant="h6">ステップ1: アプリをインストール</Typography>
        <Typography paragraph>makeshopの管理画面からアプリをインストールしてください。</Typography>
      </Box>

      <Box sx={{ mt: 3 }}>
        <Typography variant="h6">ステップ2: ショップIDを登録</Typography>
        <Typography paragraph>インストール完了後、ショップIDを以下に入力して保存してください。</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-start' }}>
          <TextField
            label="ショップID"
            variant="outlined"
            value={shopId}
            onChange={(e) => setShopId(e.target.value)}
            disabled={isLoading}
          />
          <Button variant="contained" onClick={handleSave} disabled={isLoading}>
            {isLoading ? <CircularProgress size={24} color="inherit" /> : 'ショップIDを保存'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

// =============================================================================
// ▼▼▼ FutureShop連携フォームコンポーネント ▼▼▼
// =============================================================================
interface FutureShopAuthFormProps {
  clientId: string;
}

const FutureShopAuthForm: React.FC<FutureShopAuthFormProps> = ({ clientId }) => {
  const [fsClientId, setFsClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [xShopKey, setXShopKey] = useState('');
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();

  const { mutate, isLoading } = useMutation(
    (variables: { fsClientId: string; clientSecret: string; xShopKey: string }) =>
      dataProvider.updateEcCredentials({
        clientId: clientId,
        data: {
          fsClientId: variables.fsClientId,
          clientSecret: variables.clientSecret,
          xShopKey: variables.xShopKey
        }
      }),
    {
      onSuccess: () => {
        notify('futureshop設定が正常に保存されました。', { type: 'success' });
        setFsClientId('');
        setClientSecret('');
        setXShopKey('');
        refresh();
      },
      onError: (error: any) => {
        notify(error.message || 'エラーが発生しました。', { type: 'error' });
      }
    }
  );

  const handleSave = () => {
    if (!fsClientId.trim() || !clientSecret.trim() || !xShopKey.trim()) {
      notify('すべての項目を入力してください。', { type: 'warning' });
      return;
    }
    mutate({ fsClientId, clientSecret, xShopKey });
  };

  return (
    <Box sx={{ border: '1px solid #ddd', padding: 3, marginY: 2, borderRadius: '4px' }}>
      <Typography variant="h5" gutterBottom>
        futureshop API連携設定
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        futureshop様から取得したAPI認証情報を入力してください。
      </Alert>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          label="クライアントID"
          variant="outlined"
          fullWidth
          value={fsClientId}
          onChange={(e) => setFsClientId(e.target.value)}
          disabled={isLoading}
        />
        <TextField
          label="クライアントシークレット"
          variant="outlined"
          fullWidth
          type="password"
          value={clientSecret}
          onChange={(e) => setClientSecret(e.target.value)}
          disabled={isLoading}
        />
        <TextField
          label="店舗KEY"
          variant="outlined"
          fullWidth
          value={xShopKey}
          onChange={(e) => setXShopKey(e.target.value)}
          disabled={isLoading}
        />
        <Button variant="contained" onClick={handleSave} disabled={isLoading} sx={{ alignSelf: 'flex-start' }}>
          {isLoading ? <CircularProgress size={24} color="inherit" /> : '認証情報を保存'}
        </Button>
      </Box>
    </Box>
  );
};

// =============================================================================
// ▼▼▼ ShopServe連携フォームコンポーネント ▼▼▼
// =============================================================================
interface ShopServeAuthFormProps {
  clientId: string;
}

const ShopServeAuthForm: React.FC<ShopServeAuthFormProps> = ({ clientId }) => {
  const [ssShopId, setSsShopId] = useState('');
  const [managerAuthKey, setManagerAuthKey] = useState('');
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();

  const { mutate, isLoading } = useMutation(
    (variables: { ssShopId: string; managerAuthKey: string }) =>
      dataProvider.updateEcCredentials({
        clientId: clientId,
        data: {
          ssShopId: variables.ssShopId,
          managerAuthKey: variables.managerAuthKey
        }
      }),
    {
      onSuccess: () => {
        notify('ショップサーブ設定が正常に保存されました。', { type: 'success' });
        setSsShopId('');
        setManagerAuthKey('');
        refresh();
      },
      onError: (error: any) => {
        notify(error.message || 'エラーが発生しました。', { type: 'error' });
      }
    }
  );

  const handleSave = () => {
    if (!ssShopId.trim() || !managerAuthKey.trim()) {
      notify('すべての項目を入力してください。', { type: 'warning' });
      return;
    }
    mutate({ ssShopId, managerAuthKey });
  };

  return (
    <Box sx={{ border: '1px solid #ddd', padding: 3, marginY: 2, borderRadius: '4px' }}>
      <Typography variant="h5" gutterBottom>
        ショップサーブ API連携設定
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        ショップサーブ管理画面から取得したAPI認証情報を入力してください。
      </Alert>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          label="ショップID"
          variant="outlined"
          fullWidth
          value={ssShopId}
          onChange={(e) => setSsShopId(e.target.value)}
          disabled={isLoading}
        />
        <TextField
          label="マネジャー認証キー"
          variant="outlined"
          fullWidth
          type="password"
          value={managerAuthKey}
          onChange={(e) => setManagerAuthKey(e.target.value)}
          disabled={isLoading}
          helperText="ショップサーブ管理画面の「ショップサーブAPI」から取得できます"
        />
        <Button variant="contained" onClick={handleSave} disabled={isLoading} sx={{ alignSelf: 'flex-start' }}>
          {isLoading ? <CircularProgress size={24} color="inherit" /> : '認証情報を保存'}
        </Button>
      </Box>
    </Box>
  );
};

// =============================================================================
// ▼▼▼ メインのページコンポーネント ▼▼▼
// =============================================================================

export const TrackingCodePage = () => {
  const [ecSiteType, setEcSiteType] = useState<string | null>(null);
  const [clientId, setClientId] = useState<string | null>(null);
  const translate = useTranslate();

  useEffect(() => {
    const type = localStorage.getItem('ecSiteType');
    const id = localStorage.getItem('clientId');
    setEcSiteType(type);
    setClientId(id);
  }, []);

  // トラッキングコードが不要なECサイトタイプ（クーポンコード運用など）
  const EC_SITES_WITHOUT_TRACKING_CODE = ['3', '5']; // STORES, EC-CUBE

  // トラッキングコードを表示するかどうかを判定
  const shouldShowTrackingCode = (siteType: string | null): boolean => {
    if (!siteType) return false;
    return !EC_SITES_WITHOUT_TRACKING_CODE.includes(siteType);
  };

  // clientId が確定するまでコンテンツを表示しないようにする
  if (!clientId) {
    return (
      <div>
        <Title title={translate(`resources.script-settings.name`)} />
        <p>読み込み中...</p>
      </div>
    );
  }

  const getContent = () => {
    if (!ecSiteType || !scriptData) {
      return <p>設定情報が不足しています...</p>;
    }

    let platformKey: string | null = null;
    let authFormComponent: React.ReactNode | null = null; // ecSiteTypeに応じて、表示するフォームとplatformKeyを決定

    if (ecSiteType === '1') {
      // Shopify
      platformKey = 'shopify';
      authFormComponent = <ShopifyAuthForm clientId={clientId} />;
    } else if (ecSiteType === '2') {
      // BASE
      platformKey = 'base';
      authFormComponent = <BaseAuthForm clientId={clientId} />;
    } else if (ecSiteType === '3') {
      // STORES
      platformKey = 'stores';
      authFormComponent = <StoresAuthForm clientId={clientId} />;
    } else if (ecSiteType === '4') {
      // カラーミーショップ
      platformKey = 'colorme';
      authFormComponent = <ColormeAuthForm clientId={clientId} />;
    } else if (ecSiteType === '5') {
      // EC-CUBE
      platformKey = 'eccube';
      authFormComponent = <EcCubeAuthForm clientId={clientId} />;
    } else if (ecSiteType === '8') {
      // makeshop
      platformKey = 'makeshop';
      authFormComponent = <MakeshopAuthForm clientId={clientId} />;
    } else if (ecSiteType === '6') {
      // FutureShop
      platformKey = 'futureshop';
      authFormComponent = <FutureShopAuthForm clientId={clientId} />;
    } else if (ecSiteType === '7') {
      // ShopServe
      platformKey = 'shopserve';
      authFormComponent = <ShopServeAuthForm clientId={clientId} />;
    } else {
      return <p>このECサイトタイプに対応する設定フォームはありません。</p>;
    } // トラッキングコードスニペットの生成（共通ロジック）

    const platformData = scriptData[platformKey];
    const trackingSnippets = platformData
      ? Object.keys(platformData).map((key) => {
          const snippet = platformData[key];
          let codeContent = snippet.code || snippet.url || '';
          if (codeContent.includes('__CLIENT_ID__')) {
            codeContent = codeContent.replace(/__CLIENT_ID__/g, clientId);
          }
          if (snippet.code) {
            codeContent = codeContent.trim();
          }
          return <CodeSnippet key={key} title={snippet.title} description={snippet.description} code={codeContent} />;
        })
      : []; // 最終的な表示内容を組み立て

    return (
      <>
        {/* ★★★ 変更: API連携テストコンポーネントをここに追加 ★★★ */}
        <ApiConnectionStatus clientId={clientId} ecSiteType={ecSiteType} />

        {/* トラッキングコードが必要なECサイトの場合のみ表示 */}
        {shouldShowTrackingCode(ecSiteType) && (
          <>
            <Typography variant="h5" sx={{ mt: 4 }}>
              トラッキングコード
            </Typography>

            {trackingSnippets.length > 0 ? (
              trackingSnippets
            ) : (
              <p>このサイトタイプに設定されたトラッキングコードはありません。</p>
            )}
            <hr />
          </>
        )}
        {authFormComponent}
      </>
    );
  };

  return (
    <div>
      <Title title={translate(`resources.script-settings.name`)} />
      {shouldShowTrackingCode(ecSiteType) && (
        <>
          <p>
            ご利用のショップに必要なコードをコピーし、指定の場所に貼り付けてください。コードは編集しないでください。
          </p>
          <hr />
        </>
      )}
      {getContent()}
    </div>
  );
};
